import { ChangeDetectionStrategy, Component, computed, inject, input, model, output } from '@angular/core';
import { dia } from '@clientio/rappid';
import { produce } from 'immer';
import { FlowchartExportService, GuidelineService } from 'shared/domain';
import { OrganisationSettingsService } from 'shared/util-common';

import { ColorCustomisationsDirective } from '@treetop/shared/color-presets';
import { FlowchartNodeType, FlowchartWidget } from '@treetop/shared/model';

import { CompareFlowchartComponent } from './compare-flowchart/compare-flowchart.component';
import { ICompareWidgetComponent } from '../../widget-component.interface';

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tt-flowchart-widget',
  templateUrl: './flowchart-widget.component.html',
  styleUrls: ['./flowchart-widget.component.scss'],
  imports: [CompareFlowchartComponent, ColorCustomisationsDirective],
})
export class FlowchartWidgetComponent implements ICompareWidgetComponent<FlowchartWidget> {
  private guidelineService = inject(GuidelineService);
  private flowchartExportService = inject(FlowchartExportService);
  private organisationSettingsService = inject(OrganisationSettingsService);
  protected organisationSettings = computed(
    () => this.guidelineService.guidelineData()?.organisation?.organisationSettings ?? this.organisationSettingsService.settings(),
  );

  public widget = input.required<FlowchartWidget>();
  public flowchartTitle = input<string>('');
  public isCompareMode = input<boolean>(false);
  public isEditMode = model<boolean>(false);
  public mergeConflictStatus = input<'conflict' | 'resolved' | undefined>();

  public resolveMergeConflict = output();
  public unresolveMergeConflict = output();

  protected elements = computed(() => {
    return this.widget().localChanges?.elements ?? this.widget().elements ?? [];
  });

  protected handleLocalChanged(elements: FlowchartNodeType[]) {
    const updatedLocalChanges = produce(this.widget().localChanges || {}, (draft) => {
      draft.elements = elements;
    });

    this.guidelineService.updateWidgetLocal<FlowchartWidget>(this.widget().uniqueId, updatedLocalChanges);
  }

  protected handleExportFlowchartClicked(paper: dia.Paper) {
    const guidelineUniqueId: string = this.guidelineService.guidelineData()?.uniqueId ?? '';
    const flowchartWidgetId: string = this.widget().uniqueId ?? '';

    this.flowchartExportService.exportAsPng(guidelineUniqueId, flowchartWidgetId, this.flowchartTitle(), paper);
  }

  protected handleExportPdfFlowchartClicked(paper: dia.Paper) {
    const guidelineUniqueId = this.guidelineService.guidelineData()?.uniqueId ?? '';
    const flowchartWidgetId = this.widget().uniqueId ?? '';

    this.flowchartExportService.exportAsPdf(guidelineUniqueId, flowchartWidgetId, this.flowchartTitle(), paper);
  }
}
